import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export type ConversationStatus = {
  id: string;
  label: string;
  color: string;
};

const COLOR_DOT_MAP: Record<string, string> = {
  blue:   "bg-blue-500",
  green:  "bg-green-500",
  yellow: "bg-yellow-400",
  red:    "bg-red-500",
  purple: "bg-purple-500",
  pink:   "bg-pink-500",
  indigo: "bg-indigo-500",
  gray:   "bg-gray-400",
};

interface Props {
  x: number;
  y: number;
  statuses: ConversationStatus[];
  currentStatus: string | null;
  onSelect: (statusId: string | null) => void;
  onClose: () => void;
}

export default function StatusContextMenu({
  x,
  y,
  statuses,
  currentStatus,
  onSelect,
  onClose,
}: Props) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  // Adjust position so menu doesn't overflow the viewport
  const menuWidth = 200;
  const menuHeight = (statuses.length + 1) * 36 + 16;
  const adjustedX = x + menuWidth > window.innerWidth ? window.innerWidth - menuWidth - 8 : x;
  const adjustedY = y + menuHeight > window.innerHeight ? window.innerHeight - menuHeight - 8 : y;

  return createPortal(
    <div
      ref={menuRef}
      style={{ top: adjustedY, left: adjustedX }}
      className="fixed z-[9999] bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[180px]"
    >
      <p className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
        Set label
      </p>

      {statuses.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => onSelect(currentStatus === s.id ? null : s.id)}
          className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition hover:bg-slate-50
            ${currentStatus === s.id ? "font-semibold text-slate-900" : "text-slate-700"}`}
        >
          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${COLOR_DOT_MAP[s.color] ?? "bg-gray-400"}`} />
          {s.label}
          {currentStatus === s.id && (
            <span className="ml-auto text-xs text-slate-400">(active)</span>
          )}
        </button>
      ))}

      {currentStatus && (
        <>
          <div className="mx-3 my-1 border-t border-slate-100" />
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-slate-500 hover:bg-slate-50 transition"
          >
            <X size={12} className="flex-shrink-0" />
            Clear label
          </button>
        </>
      )}
    </div>,
    document.body
  );
}
